import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  MessageSquare, Mail, RefreshCw, Send, CheckCircle2, Clock, XCircle, Search,
  AlertTriangle, Link2, ShieldCheck,
} from 'lucide-react';

const STATUS_META = {
  APPROVED: { color: '#16A34A', bg: '#DCFCE7', icon: CheckCircle2 },
  SUBMITTED: { color: '#D97706', bg: '#FEF3C7', icon: Clock },
  PENDING: { color: '#D97706', bg: '#FEF3C7', icon: Clock },
  REJECTED: { color: '#DC2626', bg: '#FEE2E2', icon: XCircle },
};
const statusMeta = (s) => STATUS_META[s] || { color: '#64748B', bg: '#F1F5F9', icon: Clock };

export default function CommunicationCentre() {
  const [tab, setTab] = useState('whatsapp');
  const [tplData, setTplData] = useState({ templates: [], meta: null });
  const [workflows, setWorkflows] = useState([]);
  const [ctxFields, setCtxFields] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [paramVals, setParamVals] = useState({});
  const [dest, setDest] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [wfEdit, setWfEdit] = useState({});
  const [emailActive, setEmailActive] = useState(null);

  const loadTemplates = () => api.get('/whatsapp/templates').then((r) => setTplData(r.data));
  const loadWorkflows = () => api.get('/whatsapp/workflows').then((r) => {
    setWorkflows(r.data.workflows); setCtxFields(r.data.context_fields);
    const e = {};
    r.data.workflows.forEach((w) => { e[w.key] = { template_id: w.gupshup_template_id || '', hpmi_template_id: w.gupshup_template_id_hpmi || '', params: [...(w.params_order || [])], media_url: w.media_url || '' }; });
    setWfEdit(e);
  });

  useEffect(() => {
    loadTemplates();
    loadWorkflows();
    api.get('/templates').then((r) => { setEmailTemplates(r.data); setEmailActive(r.data[0]); });
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post('/whatsapp/templates/refresh');
      setTplData(r.data);
      const c = r.data.sync?.counts;
      if (r.data.sync?.error) toast.error(`Sync failed: ${r.data.sync.error}`);
      else if (c) toast.success(`Synced — ${c.total} total, ${c.approved} approved`);
      else toast.info('Templates refreshed');
      loadWorkflows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Refresh failed'); }
    finally { setRefreshing(false); }
  };

  const meta = tplData.meta || {};
  const approved = tplData.templates || [];
  const filtered = useMemo(() => approved.filter((t) =>
    `${t.name} ${t.category} ${t.languageCode} ${t.templateType}`.toLowerCase().includes(search.toLowerCase())),
  [approved, search]);

  const pickTemplate = (t) => { setSelected(t); setParamVals({}); setSendResult(null); };
  const allParamsFilled = selected ? (selected.variables || []).every((v) => (paramVals[v] || '').trim()) : false;

  const sendTest = async () => {
    if (!selected) return;
    setSending(true); setSendResult(null);
    try {
      const params = selected.variables.map((v) => paramVals[v] || '');
      const r = await api.post('/whatsapp/test', { destination: dest, template_id: selected.gupshupTemplateId, params });
      setSendResult({ ok: r.data.status === 'SUBMITTED', data: r.data });
      if (r.data.status === 'SUBMITTED') toast.success(`Submitted · messageId ${r.data.provider_message_id}`);
      else toast.error(r.data.error || 'Send failed');
    } catch (e) {
      const d = e.response?.data;
      setSendResult({ ok: false, data: { error: d?.detail || 'Send failed' } });
      toast.error(d?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const saveWorkflow = async (w) => {
    const edit = wfEdit[w.key] || {};
    try {
      await api.put(`/whatsapp/workflows/${w.key}`, { gupshup_template_id: edit.template_id, gupshup_template_id_hpmi: edit.hpmi_template_id || '', params_order: edit.params, media_url: edit.media_url || '' });
      toast.success(`Workflow "${w.label}" mapped`);
      loadWorkflows();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const confirmWorkflow = async (w) => {
    try { await api.post(`/whatsapp/workflows/${w.key}/confirm`); toast.success('Mapping confirmed'); loadWorkflows(); }
    catch (e) { toast.error('Confirm failed'); }
  };
  const selWfTemplate = (key, tid) => {
    const tpl = approved.find((t) => t.gupshupTemplateId === tid);
    setWfEdit((p) => ({ ...p, [key]: { template_id: tid, params: (tpl?.variables || []).map((v) => p[key]?.params?.[Number(v) - 1] || '') } }));
  };

  const inputCls = 'rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-sm outline-none focus:border-action';

  return (
    <div className="space-y-5" data-testid="communications-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#172033]">Communication Centre</h1>
          <p className="text-sm text-[#64748B]">WhatsApp templates sync from Gupshup (single source of truth). Email templates are managed locally.</p>
        </div>
        <div className="flex gap-1 rounded-xl border border-[#E2E8F0] bg-white p-1">
          {[['whatsapp', 'WhatsApp', MessageSquare], ['workflows', 'Automation', Link2], ['email', 'Email', Mail]].map(([k, l, Icon]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition ${tab === k ? 'bg-navy text-white' : 'text-[#475569] hover:bg-[#F1F5F9]'}`}>
              <Icon size={15} /> {l}
            </button>
          ))}
        </div>
      </div>

      {tab === 'whatsapp' && (
        <div className="space-y-4">
          {/* Template Health */}
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="template-health">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-5">
                <div><p className="text-xs text-[#94A3B8]">Total</p><p className="font-heading text-xl font-bold text-[#172033]" data-testid="health-total">{meta.count_total ?? 0}</p></div>
                <div><p className="text-xs text-[#16A34A]">Approved</p><p className="font-heading text-xl font-bold text-[#16A34A]" data-testid="health-approved">{meta.count_approved ?? 0}</p></div>
                <div><p className="text-xs text-[#D97706]">Pending</p><p className="font-heading text-xl font-bold text-[#D97706]" data-testid="health-pending">{meta.count_pending ?? 0}</p></div>
                <div><p className="text-xs text-[#DC2626]">Rejected</p><p className="font-heading text-xl font-bold text-[#DC2626]" data-testid="health-rejected">{meta.count_rejected ?? 0}</p></div>
                <div className="border-l border-[#E2E8F0] pl-5">
                  <p className="text-xs text-[#94A3B8]">Last synced</p>
                  <p className="text-sm font-medium text-[#475569]" data-testid="health-last-synced">{meta.lastSyncedAt ? new Date(meta.lastSyncedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}</p>
                </div>
              </div>
              <button data-testid="refresh-templates" onClick={refresh} disabled={refreshing}
                className="flex items-center gap-2 rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-dark disabled:opacity-50">
                <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Syncing…' : 'Refresh Templates'}
              </button>
            </div>
            {meta.lastSyncError && <p className="mt-2 flex items-center gap-1 text-xs text-[#DC2626]"><AlertTriangle size={12} /> Last sync error: {meta.lastSyncError}</p>}
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {/* Template list (approved only) */}
            <div className="rounded-xl border border-[#E2E8F0] bg-white p-2">
              <div className="relative p-1">
                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input data-testid="template-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search approved templates…"
                  className="w-full rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] py-2 pl-8 pr-3 text-sm outline-none focus:border-action" />
              </div>
              <div className="hpmi-scroll-thin max-h-[60vh] overflow-y-auto p-1">
                {filtered.length === 0 && <p className="p-4 text-center text-xs text-[#94A3B8]" data-testid="no-approved-templates">No approved Gupshup templates yet. Once WhatsApp approves a template in Gupshup, click Refresh Templates.</p>}
                {filtered.map((t) => {
                  const m = statusMeta(t.status);
                  return (
                    <button key={t.gupshupTemplateId} data-testid={`template-${t.name}`} onClick={() => pickTemplate(t)}
                      className={`w-full rounded-lg px-3 py-2.5 text-left text-sm transition ${selected?.gupshupTemplateId === t.gupshupTemplateId ? 'bg-action/10' : 'hover:bg-[#F8FAFC]'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`font-semibold ${selected?.gupshupTemplateId === t.gupshupTemplateId ? 'text-action' : 'text-[#172033]'}`}>{t.name}</span>
                        <span className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase" style={{ color: m.color, backgroundColor: m.bg }}><m.icon size={10} />{t.status}</span>
                      </div>
                      <p className="mt-0.5 text-xs text-[#64748B]">{t.category || '—'} · {t.languageCode || '—'} · {t.templateType || '—'}{t.quality ? ` · ${t.quality}` : ''}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Preview + test send */}
            <div className="lg:col-span-2 rounded-xl border border-[#E2E8F0] bg-white p-6">
              {!selected ? (
                <div className="flex h-full flex-col items-center justify-center py-12 text-center">
                  <MessageSquare size={30} className="text-[#CBD5E1]" />
                  <p className="mt-2 text-sm text-[#94A3B8]">Select an approved template to preview and send.</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="template-preview">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h2 className="font-heading text-lg font-bold text-[#172033]">{selected.name}</h2>
                      <p className="text-xs text-[#64748B]">ID <span className="font-mono text-action">{selected.gupshupTemplateId}</span> · {selected.category} · {selected.languageCode} · {selected.templateType}{selected.quality ? ` · Quality ${selected.quality}` : ''}</p>
                    </div>
                    <span className="flex items-center gap-1 rounded-full bg-[#DCFCE7] px-2.5 py-1 text-xs font-bold text-[#16A34A]"><ShieldCheck size={12} /> APPROVED</span>
                  </div>
                  <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                    {selected.header && <p className="mb-2 font-semibold text-[#172033]">{selected.header}</p>}
                    <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-[#475569]" data-testid="preview-body">{selected.body || '(no body)'}</pre>
                    {selected.footer && <p className="mt-2 text-xs text-[#94A3B8]">{selected.footer}</p>}
                    {(selected.buttons || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {selected.buttons.map((b, i) => <span key={i} className="rounded-lg border border-action/30 bg-white px-3 py-1.5 text-xs font-semibold text-action">{b.text || b.type}</span>)}
                      </div>
                    )}
                  </div>

                  {(selected.variables || []).length > 0 && (
                    <div>
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Parameters ({selected.variables.length})</p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {selected.variables.map((v) => (
                          <label key={v} className="block">
                            <span className="mb-1 block text-xs font-semibold text-[#475569]">{`{{${v}}}`}</span>
                            <input data-testid={`param-${v}`} className={`${inputCls} w-full`} value={paramVals[v] || ''} onChange={(e) => setParamVals((p) => ({ ...p, [v]: e.target.value }))} placeholder={`Value for {{${v}}}`} />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Send (real Gupshup)</p>
                    <div className="flex flex-wrap gap-2">
                      <input data-testid="test-destination" className={`${inputCls} flex-1`} placeholder="Recipient WhatsApp number e.g. 9330629644" value={dest} onChange={(e) => setDest(e.target.value)} />
                      <button data-testid="send-test" onClick={sendTest} disabled={sending || !dest.trim() || !allParamsFilled}
                        className="flex items-center gap-2 rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-dark disabled:opacity-50">
                        <Send size={14} /> {sending ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                    {!allParamsFilled && (selected.variables || []).length > 0 && <p className="mt-1.5 text-xs text-[#D97706]">Fill every {`{{n}}`} parameter to enable sending.</p>}
                    {sendResult && (
                      <pre data-testid="send-result" className={`mt-3 max-h-56 overflow-auto rounded-lg border p-3 text-xs ${sendResult.ok ? 'border-[#BBF7D0] bg-[#F0FDF4] text-[#166534]' : 'border-[#FECACA] bg-[#FEF2F2] text-[#991B1B]'}`}>{JSON.stringify(sendResult.data, null, 2)}</pre>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'workflows' && (
        <div className="space-y-3" data-testid="workflow-automation">
          <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4 text-xs text-[#64748B]">
            Map each automated admission workflow to a Gupshup-approved template and choose which applicant field fills each {`{{n}}`} parameter. Unmapped workflows stay QUEUED. If Gupshup reports a template change, re-confirm the mapping before auto-send resumes.
          </div>
          {workflows.map((w) => {
            const edit = wfEdit[w.key] || { template_id: '', params: [] };
            const tpl = approved.find((t) => t.gupshupTemplateId === edit.template_id);
            const mapped = Boolean(w.gupshup_template_id);
            return (
              <div key={w.key} className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid={`workflow-${w.key}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-[#172033]">{w.label}</p>
                      {mapped && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${w.template_status === 'APPROVED' ? 'bg-[#DCFCE7] text-[#16A34A]' : 'bg-[#FEE2E2] text-[#DC2626]'}`}>{w.template_status || 'UNMAPPED'}</span>}
                      {w.needs_review && <span className="rounded-full bg-[#FEF3C7] px-2 py-0.5 text-[10px] font-bold uppercase text-[#D97706]">Template changed — review</span>}
                    </div>
                    <p className="text-xs text-[#64748B]">{w.description}</p>
                  </div>
                  {w.needs_review && <button data-testid={`confirm-${w.key}`} onClick={() => confirmWorkflow(w)} className="flex items-center gap-1 rounded-lg bg-[#FEF3C7] px-3 py-1.5 text-xs font-semibold text-[#D97706] hover:bg-[#FDE68A]"><CheckCircle2 size={13} /> Confirm mapping</button>}
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold text-[#475569]">Gupshup-approved template</span>
                    <select data-testid={`wf-template-${w.key}`} className={`${inputCls} w-full`} value={edit.template_id} onChange={(e) => selWfTemplate(w.key, e.target.value)}>
                      <option value="">— Not mapped —</option>
                      {approved.map((t) => <option key={t.gupshupTemplateId} value={t.gupshupTemplateId}>{t.name} ({t.category})</option>)}
                    </select>
                  </label>
                  {(tpl?.variables || []).length > 0 && (
                    <div className="grid grid-cols-2 gap-2">
                      {tpl.variables.map((v, i) => (
                        <label key={v} className="block">
                          <span className="mb-1 block text-xs font-semibold text-[#475569]">{`{{${v}}}`} →</span>
                          <select data-testid={`wf-param-${w.key}-${v}`} className={`${inputCls} w-full`} value={edit.params[i] || ''}
                            onChange={(e) => setWfEdit((p) => { const arr = [...(p[w.key]?.params || [])]; arr[i] = e.target.value; return { ...p, [w.key]: { ...p[w.key], params: arr } }; })}>
                            <option value="">— field —</option>
                            {ctxFields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                          </select>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {tpl?.templateType === 'DOCUMENT' && (
                  <div className="mt-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                    <span className="mb-1 block text-xs font-semibold text-[#475569]">Document to attach (PDF)</span>
                    <input
                      data-testid={`wf-media-${w.key}`}
                      className={`${inputCls} w-full`}
                      placeholder="Leave empty for default: HPMI Admission Test Syllabus PDF"
                      value={edit.media_url || ''}
                      onChange={(e) => setWfEdit((p) => ({ ...p, [w.key]: { ...p[w.key], media_url: e.target.value } }))}
                    />
                    <p className="mt-1 text-[11px] text-[#94A3B8]">Public HTTPS PDF URL. If empty, the compiled Admission Test Syllabus PDF is sent.</p>
                  </div>
                )}
                {w.key === 'admission_approved' && (
                  <div className="mt-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                    <span className="mb-1 block text-xs font-semibold text-[#475569]">HPMI welcome template — Class 1–9 (Montessori classes use the primary mapping above)</span>
                    <select data-testid={`wf-template-hpmi-${w.key}`} className={`${inputCls} w-full`} value={edit.hpmi_template_id || ''}
                      onChange={(e) => setWfEdit((p) => ({ ...p, [w.key]: { ...p[w.key], hpmi_template_id: e.target.value } }))}>
                      <option value="">— Not mapped —</option>
                      {approved.map((t) => <option key={t.gupshupTemplateId} value={t.gupshupTemplateId}>{t.name} ({t.category})</option>)}
                    </select>
                    <p className="mt-1 text-[11px] text-[#94A3B8]" data-testid={`wf-hpmi-status-${w.key}`}>
                      {w.hpmi_template_name ? `${w.hpmi_template_name} — ${w.hpmi_template_status || 'unknown'}` : 'Select the approved welcome_letter_hpmi template. Approval auto-selects by class.'}
                    </p>
                  </div>
                )}
                <button data-testid={`wf-save-${w.key}`} onClick={() => saveWorkflow(w)} className="mt-3 rounded-lg bg-action px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-action-hover">Save Mapping</button>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'email' && (
        <div className="grid gap-4 lg:grid-cols-3" data-testid="email-templates">
          <div className="rounded-xl border border-[#E2E8F0] bg-white p-2">
            <div className="hpmi-scroll-thin max-h-[70vh] overflow-y-auto">
              {emailTemplates.map((t) => (
                <button key={t.key} data-testid={`email-template-${t.key}`} onClick={() => setEmailActive(t)}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm ${emailActive?.key === t.key ? 'bg-action/10 font-semibold text-action' : 'text-[#475569] hover:bg-[#F8FAFC]'}`}>
                  <Mail size={15} /> {t.name}
                </button>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2 rounded-xl border border-[#E2E8F0] bg-white p-6">
            {emailActive && (
              <>
                <div className="mb-4 flex items-center gap-2"><Mail size={18} className="text-action" /><h2 className="font-heading text-lg font-bold text-[#172033]">{emailActive.name}</h2></div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Subject</p>
                <p className="mb-4 rounded-lg bg-[#F8FAFC] px-4 py-3 text-sm font-medium text-[#172033]">{emailActive.subject}</p>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#94A3B8]">Message Body</p>
                <pre className="whitespace-pre-wrap rounded-lg bg-[#F8FAFC] px-4 py-3 font-sans text-sm leading-relaxed text-[#475569]">{emailActive.body}</pre>
                <p className="mt-4 text-xs text-[#94A3B8]">Placeholders like {'{applicant_name}'} and {'{class_name}'} are filled automatically per applicant.</p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
